import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Page, Banner, Spinner, BlockStack, Text, Box } from '@shopify/polaris';
import { useApi } from '../providers/ApiProvider';
import { t } from '../i18n';

/**
 * Shopify Admin → Discounts → click Freebi app discount
 * lands here via Function ui.paths.details = /discounts/:id
 * We resolve the Shopify discount node → Freebi campaign and open the editor.
 */
export function DiscountRedirectPage() {
  const { id, functionId } = useParams<{ id?: string; functionId?: string }>();
  const navigate = useNavigate();
  const api = useApi();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const raw = id || functionId || '';
    if (!raw) {
      navigate('/campaigns', { replace: true });
      return;
    }

    // Shopify may pass full GID or only the numeric / last path segment
    let shopifyId = decodeURIComponent(raw);
    try {
      shopifyId = decodeURIComponent(shopifyId);
    } catch {
      /* already decoded */
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{
          campaignId?: string | null;
          found?: boolean;
        }>(`/discounts/resolve?shopifyId=${encodeURIComponent(shopifyId)}`);
        if (cancelled) return;
        if (res.campaignId) {
          navigate(`/campaigns/${res.campaignId}`, { replace: true });
          return;
        }
        setError(
          t("Could not find a Freebi campaign for this discount. Open Campaigns and edit it there."),
        );
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to open campaign');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, functionId, api, navigate]);

  return (
    <Page title={t("Opening Freebi campaign…")}>
      <Box padding="400">
        <BlockStack gap="300" inlineAlign="center">
          {!error && <Spinner accessibilityLabel={t("Loading campaign")} size="large" />}
          {error ? (
            <Banner tone="warning" title={t("Discount not linked")}>
              <p>{error}</p>
              <p>
                <button
                  type="button"
                  onClick={() => navigate('/campaigns')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2c6ecb',
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                >
                  {t("Go to Campaigns")}
                </button>
              </p>
            </Banner>
          ) : (
            <Text as="p" tone="subdued">
              {t("Loading campaign settings…")}
            </Text>
          )}
        </BlockStack>
      </Box>
    </Page>
  );
}
